import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Connects to data-controller="daterange"
export default class extends Controller {
  static targets = ["startDate", "endDate", "range"]
  static values = { 
    startDate: String, 
    endDate: String,
    locale: { type: String, default: "zh" },
    minDate: String,
    maxDate: String
  }

  connect() {
    console.log("Daterange controller connected")
    this.initializeDateRange()
  }

  disconnect() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy()
    }
  }

  initializeDateRange() {
    const config = {
      mode: "range",
      dateFormat: "Y-m-d",
      locale: this.localeValue === "zh" ? this.getChineseLocale() : "default",
      allowInput: true,
      clickOpens: true,
      closeOnSelect: false, // 不要在选择第一个日期后立即关闭
      onChange: this.handleDateChange.bind(this),
      onReady: this.handleReady.bind(this),
      onClose: this.handleClose.bind(this)
    }

    // 设置日期范围限制
    if (this.minDateValue) {
      config.minDate = this.minDateValue
    }
    if (this.maxDateValue) {
      config.maxDate = this.maxDateValue
    }

    // 如果有初始值，设置默认日期
    if (this.startDateValue && this.endDateValue) {
      config.defaultDate = [this.startDateValue, this.endDateValue]
    } else if (this.startDateValue) {
      config.defaultDate = this.startDateValue
    }

    // 初始化flatpickr
    if (this.hasRangeTarget) {
      console.log("Initializing flatpickr on", this.rangeTarget)
      this.flatpickrInstance = flatpickr(this.rangeTarget, config)
    } else {
      console.error("No range target found")
    }
  }

  handleDateChange(selectedDates, dateStr, instance) {
    if (selectedDates.length === 2) {
      // 选择了日期范围
      const startDate = this.formatDate(selectedDates[0])
      const endDate = this.formatDate(selectedDates[1])
      
      if (this.hasStartDateTarget) {
        this.startDateTarget.value = startDate
      }
      if (this.hasEndDateTarget) {
        this.endDateTarget.value = endDate
      }
      
      // 更新显示的日期范围文本
      if (this.hasRangeTarget) {
        this.rangeTarget.value = `${startDate} 至 ${endDate}`
      }
      
      // 触发表单提交或其他操作
      this.dispatch("daterangeChanged", { 
        detail: { startDate, endDate, selectedDates }
      })
      
      // 可选：自动提交表单
      setTimeout(() => {
        this.autoSubmit()
      }, 500)
    } else if (selectedDates.length === 1) {
      // 只选择了开始日期
      const startDate = this.formatDate(selectedDates[0])
      if (this.hasStartDateTarget) {
        this.startDateTarget.value = startDate
      }
      if (this.hasEndDateTarget) {
        this.endDateTarget.value = ""
      }
      if (this.hasRangeTarget) {
        this.rangeTarget.value = startDate
      }
    } else if (selectedDates.length === 0) {
      // 清除选择
      if (this.hasStartDateTarget) {
        this.startDateTarget.value = ""
      }
      if (this.hasEndDateTarget) {
        this.endDateTarget.value = ""
      }
      if (this.hasRangeTarget) {
        this.rangeTarget.value = ""
      }
      
      // 清除时也自动提交
      setTimeout(() => {
        this.autoSubmit()
      }, 500)
    }
  }

  handleReady(selectedDates, dateStr, instance) {
    // 添加快捷选择按钮
    this.addQuickSelectButtons(instance)
  }

  addQuickSelectButtons(instance) {
    const calendar = instance.calendarContainer
    
    // 创建快捷按钮容器
    const quickSelectDiv = document.createElement('div')
    quickSelectDiv.className = 'flatpickr-quick-select'
    quickSelectDiv.innerHTML = `
      <button type="button" class="quick-select-btn" data-range="all">全部</button>
      <button type="button" class="quick-select-btn" data-range="thisMonth">本月</button>
      <button type="button" class="quick-select-btn" data-range="lastMonth">上月</button>
      <button type="button" class="quick-select-btn" data-range="last30days">最近30天</button>
      <button type="button" class="quick-select-btn" data-range="clear">清除</button>
    `
    
    calendar.appendChild(quickSelectDiv)
    
    // 添加事件监听
    quickSelectDiv.addEventListener('click', (e) => {
      if (e.target.classList.contains('quick-select-btn')) {
        const range = e.target.dataset.range
        this.selectQuickRange(range, instance)
      }
    })
  }

  selectQuickRange(range, instance) {
    const today = new Date()
    let startDate, endDate
    
    // 获取数据的实际范围
    const minDate = this.minDateValue ? new Date(this.minDateValue) : null
    const maxDate = this.maxDateValue ? new Date(this.maxDateValue) : null
    
    switch (range) {
      case 'all':
        // 显示全部数据的时间范围
        if (minDate && maxDate) {
          startDate = minDate
          endDate = maxDate
        }
        break
      case 'thisMonth':
        // 使用数据范围内的当前月
        if (maxDate) {
          startDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
          endDate = maxDate
        }
        break
      case 'lastMonth':
        // 使用数据范围内的上个月
        if (maxDate) {
          const lastMonth = new Date(maxDate.getFullYear(), maxDate.getMonth() - 1, 1)
          const lastMonthEnd = new Date(maxDate.getFullYear(), maxDate.getMonth(), 0)
          startDate = lastMonth
          endDate = lastMonthEnd
        }
        break
      case 'last30days':
        // 从最大日期往前30天
        if (maxDate) {
          startDate = new Date(maxDate)
          startDate.setDate(startDate.getDate() - 29)
          endDate = maxDate
        }
        break
      case 'clear':
        instance.clear()
        if (this.hasStartDateTarget) this.startDateTarget.value = ""
        if (this.hasEndDateTarget) this.endDateTarget.value = ""
        if (this.hasRangeTarget) this.rangeTarget.value = ""
        // 清除后自动提交
        setTimeout(() => {
          this.autoSubmit()
        }, 300)
        return
    }
    
    if (startDate && endDate) {
      // 确保日期在允许范围内
      if (minDate && startDate < minDate) startDate = minDate
      if (maxDate && endDate > maxDate) endDate = maxDate
      
      instance.setDate([startDate, endDate])
      // 快捷选择后自动提交
      setTimeout(() => {
        this.autoSubmit()
      }, 300)
    }
  }

  formatDate(date) {
    return date.toISOString().split('T')[0]
  }

  getChineseLocale() {
    return {
      weekdays: {
        shorthand: ["日", "一", "二", "三", "四", "五", "六"],
        longhand: ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"]
      },
      months: {
        shorthand: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
        longhand: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"]
      },
      rangeSeparator: " 至 ",
      scrollTitle: "滚动切换",
      toggleTitle: "点击切换"
    }
  }

  handleClose(selectedDates, dateStr, instance) {
    // 当日历关闭时，如果有选择的日期范围，自动提交表单
    if (selectedDates.length === 2) {
      setTimeout(() => {
        this.autoSubmit()
      }, 100)
    }
  }

  // 手动触发表单提交的方法
  autoSubmit() {
    const form = this.element.closest('form')
    if (form) {
      form.submit()
    }
  }
}
